import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, Clock, Star, BookOpen, Target } from 'lucide-react';
import { studentAPI } from '../../services/api';

const Recommendations = () => {
    const navigate = useNavigate();
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');

    useEffect(() => {
        fetchRecommendations();
    }, []);

    const fetchRecommendations = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await studentAPI.getRecommendations();
            setRecommendations(response.data || []);
        } catch (err) {
            setError('Failed to load recommendations');
            console.error('Error fetching recommendations:', err);
        } finally {
            setLoading(false);
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'personalized':
                return <Target size={20} color="#9C27B0" />;
            case 'trending':
                return <TrendingUp size={20} color="#FF9800" />;
            case 'continue':
                return <Clock size={20} color="#27B05B" />;
            case 'popular':
                return <Star size={20} color="#FFD700" />;
            default:
                return <BookOpen size={20} color="#3498DB" />;
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'personalized':
                return '#9C27B0';
            case 'trending':
                return '#FF9800';
            case 'continue':
                return '#27B05B';
            case 'popular':
                return '#FFD700';
            default:
                return '#3498DB';
        }
    };

    const getMatchScore = (score) => {
        if (score >= 90) return { text: 'Perfect Match', color: '#27B05B' };
        if (score >= 75) return { text: 'Great Match', color: '#3498DB' };
        if (score >= 60) return { text: 'Good Match', color: '#FF9800' };
        return { text: 'Worth Trying', color: '#95A5A6' };
    };

    const filteredRecommendations = activeCategory === 'all' 
        ? recommendations 
        : recommendations.filter(rec => rec.category === activeCategory);

    const categories = [
        { id: 'all', label: 'All', count: recommendations.length },
        { id: 'personalized', label: 'For You', count: recommendations.filter(r => r.category === 'personalized').length },
        { id: 'trending', label: 'Trending', count: recommendations.filter(r => r.category === 'trending').length },
        { id: 'continue', label: 'Continue Learning', count: recommendations.filter(r => r.category === 'continue').length },
        { id: 'popular', label: 'Popular', count: recommendations.filter(r => r.category === 'popular').length }
    ];

    const renderReasonTag = (reason) => {
        const reasonStyles = {
            'Based on your skill level': { bg: '#E3F2FD', color: '#1976D2' },
            'Matches your interests': { bg: '#F3E5F5', color: '#7B1FA2' },
            'Complements current courses': { bg: '#E8F5E9', color: '#388E3C' },
            'High completion rate': { bg: '#FFF3E0', color: '#F57C00' },
            'trending': { bg: '#FFE0B2', color: '#E65100' }
        };

        const style = reasonStyles[reason] || { bg: '#F5F5F5', color: '#666' };
        
        return (
            <span style={{
                ...styles.reasonTag,
                backgroundColor: style.bg,
                color: style.color
            }}>
                {reason}
            </span>
        );
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Finding courses perfect for you...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>
                    <p>{error}</p>
                    <button onClick={fetchRecommendations} style={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.titleSection}>
                    <Sparkles size={40} color="#27B05B" />
                    <div>
                        <h1 style={styles.title}>Recommended for You</h1>
                        <p style={styles.subtitle}>Curated courses based on your learning progress and preferences</p>
                    </div>
                </div>
            </div>

            <div style={styles.categoryTabs}>
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        style={{
                            ...styles.categoryTab,
                            ...(activeCategory === cat.id ? styles.categoryTabActive : {})
                        }}
                    >
                        {cat.label} <span style={styles.categoryCount}>({cat.count})</span>
                    </button>
                ))}
            </div>

            {filteredRecommendations.length === 0 ? (
                <div style={styles.empty}>
                    <Sparkles size={64} color="#ccc" />
                    <h2 style={styles.emptyTitle}>No recommendations yet</h2>
                    <p style={styles.emptyText}>
                        Keep learning to get personalized course recommendations!
                    </p>
                    <button onClick={() => navigate('/courses')} style={styles.browseButton}>
                        Browse All Courses
                    </button>
                </div>
            ) : (
                <div style={styles.recommendationsGrid}>
                    {filteredRecommendations.map(rec => {
                        const matchInfo = getMatchScore(rec.match_score || 75);
                        
                        return (
                            <div key={rec.id} style={styles.recommendationCard}>
                                <div style={styles.cardHeader}>
                                    <div style={{
                                        ...styles.categoryBadge,
                                        backgroundColor: `${getCategoryColor(rec.category)}20`,
                                        color: getCategoryColor(rec.category)
                                    }}>
                                        {getCategoryIcon(rec.category)}
                                        <span style={{marginLeft: '6px', textTransform: 'capitalize'}}>
                                            {rec.category}
                                        </span>
                                    </div>
                                    
                                    <div style={{
                                        ...styles.matchBadge,
                                        backgroundColor: `${matchInfo.color}20`,
                                        color: matchInfo.color
                                    }}>
                                        {rec.match_score || 75}% Match
                                    </div>
                                </div>

                                {rec.image_url && (
                                    <div style={styles.courseImage}>
                                        <img src={rec.image_url} alt={rec.title} style={styles.image} />
                                    </div>
                                )}

                                <div style={styles.cardContent}>
                                    <h3 style={styles.courseTitle}>{rec.title}</h3>
                                    <p style={styles.courseDescription}>
                                        {rec.description?.substring(0, 120)}
                                        {rec.description?.length > 120 ? '...' : ''}
                                    </p>

                                    {rec.reasons && rec.reasons.length > 0 && (
                                        <div style={styles.reasonsSection}>
                                            <strong style={styles.reasonsTitle}>Why we recommend this:</strong>
                                            <div style={styles.reasonsList}>
                                                {rec.reasons.map((reason, idx) => (
                                                    <div key={idx}>
                                                        {renderReasonTag(reason)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div style={styles.courseStats}>
                                        <div style={styles.statItem}>
                                            <BookOpen size={16} color="#666" />
                                            <span>{rec.lessons_count || 0} lessons</span>
                                        </div>
                                        <div style={styles.statItem}>
                                            <Clock size={16} color="#666" />
                                            <span>{rec.duration_hours || 0}h</span>
                                        </div>
                                        <div style={styles.statItem}>
                                            <Star size={16} color="#FFD700" />
                                            <span>{rec.rating || 4.5} ({rec.reviews_count || 0})</span>
                                        </div>
                                    </div>

                                    <div style={styles.cardFooter}>
                                        {rec.price > 0 ? (
                                            <div style={styles.priceSection}>
                                                <span style={styles.price}>₹{rec.price}</span>
                                                {rec.original_price && (
                                                    <span style={styles.originalPrice}>₹{rec.original_price}</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span style={styles.freeTag}>Free</span>
                                        )}

                                        <button
                                            onClick={() => navigate(`/courses/${rec.course_id}`)}
                                            style={styles.viewButton}
                                        >
                                            View Course
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        marginBottom: '30px'
    },
    titleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    subtitle: {
        fontSize: '16px',
        color: '#666',
        marginTop: '8px'
    },
    categoryTabs: {
        display: 'flex',
        gap: '10px',
        marginBottom: '30px',
        flexWrap: 'wrap'
    },
    categoryTab: {
        padding: '12px 20px',
        backgroundColor: 'white',
        border: '2px solid #E0E0E0',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#666',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    categoryTabActive: {
        backgroundColor: '#27B05B',
        borderColor: '#27B05B',
        color: 'white'
    },
    categoryCount: {
        marginLeft: '8px',
        fontSize: '13px',
        opacity: 0.8
    },
    recommendationsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '25px'
    },
    recommendationCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '15px',
        backgroundColor: '#FAFAFA'
    },
    categoryBadge: {
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600'
    },
    matchBadge: {
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '700'
    },
    courseImage: {
        width: '100%',
        height: '200px',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    cardContent: {
        padding: '25px'
    },
    courseTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '10px'
    },
    courseDescription: {
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.6',
        marginBottom: '15px'
    },
    reasonsSection: {
        marginBottom: '15px',
        padding: '15px',
        backgroundColor: '#F9F9F9',
        borderRadius: '8px'
    },
    reasonsTitle: {
        fontSize: '13px',
        color: '#666',
        display: 'block',
        marginBottom: '10px'
    },
    reasonsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    reasonTag: {
        padding: '6px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
    },
    courseStats: {
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    statItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        color: '#666'
    },
    cardFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '15px',
        borderTop: '1px solid #E0E0E0'
    },
    priceSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    price: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#27B05B'
    },
    originalPrice: {
        fontSize: '16px',
        color: '#999',
        textDecoration: 'line-through'
    },
    freeTag: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#27B05B'
    },
    viewButton: {
        padding: '12px 30px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    error: {
        textAlign: 'center',
        padding: '50px',
        color: '#E74C3C'
    },
    retryButton: {
        marginTop: '20px',
        padding: '12px 30px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    empty: {
        textAlign: 'center',
        padding: '80px 20px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    emptyTitle: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#2C3E50',
        marginTop: '20px',
        marginBottom: '10px'
    },
    emptyText: {
        fontSize: '16px',
        color: '#666',
        marginBottom: '30px'
    },
    browseButton: {
        padding: '14px 40px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer'
    }
};

export default Recommendations;
